import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react"
import ModalContext from "./ModalContext"
import AlertModal from "components/modal/AlertModal"
import ConfirmationModal from "components/modal/ConfirmationModal"

const defaultInfo = {
  open: false,
  type: "alert",
  title: "",
  message: "",
  confirmText: "ตกลง",
  cancelText: "ยกเลิก",
  variant: "primary",
  isValid: true,
  children: null,
  onClose: () => {},
  onConfirm: () => {},
  onCancel: () => {},
}

export const ModalProvider = ({ children, isValid }) => {
  const [modalState, setModalState] = useState(defaultInfo)

  const hideModal = useCallback(() => {
    setModalState(prev => ({ ...prev, open: false }))
    setTimeout(() => setModalState(defaultInfo), 300)
  }, [])

  const setModalValidity = useCallback(isValid => {
    setModalState(prev => ({ ...prev, isValid }))
  }, [])

  const showModal = useCallback(config => {
    const newConfig = {
      ...defaultInfo,
      ...config,
      message: config.message || config.content,
      variant: config.variant || config.mode || "primary",
      open: true,
    }
    setModalState(newConfig)
  }, [])

  const createModalPromise = useCallback(
    config => {
      return new Promise(resolve => {
        showModal({
          ...config,
          onConfirm: (...args) => {
            config.onConfirm?.(...args)
            resolve(true)
            hideModal()
          },
          onCancel: (...args) => {
            config.onCancel?.(...args)
            resolve(false)
            hideModal()
          },
          onClose: (...args) => {
            config.onClose?.(...args)
            resolve(false)
            hideModal()
          },
        })
      })
    },
    [showModal, hideModal]
  )

  const modalActions = useMemo(
    () => ({
      alert: {
        show: config => showModal({ ...config, type: "alert" }),
        primary: config =>
          createModalPromise({
            type: "alert",
            variant: "primary",
            ...config,
          }),
        success: config =>
          createModalPromise({
            type: "alert",
            variant: "success",
            title: "สำเร็จ",
            ...config,
          }),
        danger: config =>
          createModalPromise({
            type: "alert",
            variant: "danger",
            title: "ผิดพลาด",
            ...config,
          }),
        warning: config =>
          createModalPromise({
            type: "alert",
            variant: "warning",
            title: "คำเตือน",
            ...config,
          }),
        info: config =>
          createModalPromise({
            type: "alert",
            variant: "info",
            title: "ข้อมูล",
            ...config,
          }),
      },
      confirm: {
        show: config => createModalPromise({ ...config, type: "confirm" }),
        primary: config =>
          createModalPromise({
            type: "confirm",
            variant: "primary",
            title: "คุณแน่ใจใช่ไหม?",
            message: "",
            ...config,
          }),
        delete: config =>
          createModalPromise({
            type: "confirm",
            variant: "danger",
            title: "ยืนยันการลบ",
            ...config,
          }),
      },
      primary: config =>
        createModalPromise({
          type: "alert",
          variant: "primary",
          ...config,
        }),
      success: config =>
        createModalPromise({
          type: config.type || "alert",
          variant: "success",
          title: "สำเร็จ",
          ...config,
        }),
      danger: config =>
        createModalPromise({
          type: config.type || "alert",
          variant: "danger",
          title: "ผิดพลาด",
          ...config,
        }),
      warning: config =>
        createModalPromise({
          type: config.type || "alert",
          variant: "warning",
          title: "คำเตือน",
          ...config,
        }),
      info: config =>
        createModalPromise({
          type: config.type || "alert",
          variant: "info",
          title: "ข้อมูล",
          ...config,
        }),
      hide: hideModal,
      setValidity: setModalValidity,
    }),
    [showModal, createModalPromise, hideModal, setModalValidity]
  )

  const handleClose = useCallback(() => {
    hideModal()
    modalState.onClose?.()
  }, [hideModal, modalState.onClose])

  const ModalComponent = useMemo(() => {
    const {
      open,
      type,
      title,
      message,
      variant,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      onClose,
      formik,
      isValid,
      className,
      size,
      children: modalChildren,
    } = modalState

    const commonProps = {
      isOpen: open,
      toggle: handleClose,
      title,
      message,
      variant,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      onClose,
      className,
      size,
      isValid,
      formik,
      children: modalChildren,
    }

    if (type == "confirm") {
      return (
        <ConfirmationModal
          {...commonProps}
          onConfirm={onConfirm || handleClose}
        />
      )
    }

    return <AlertModal {...commonProps} onConfirm={onConfirm || handleClose} />
  }, [modalState, handleClose])

  const contextValue = useMemo(
    () => ({
      modal: modalActions,
    }),
    [modalActions]
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {modalState.open && ModalComponent}
      {children}
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  return useContext(ModalContext)
}
